import type { CapturePhotoRow } from '../../../data/database.types';
import type { ImageRasterPort } from '../../../platform/image-raster';
import { archiveAxesSwapped, serverPhotoObjectPath } from '../../../data/photos/photo-images';
import type { PhotoImageUrlPort } from '../../../data/photos/photo-images';
import type { PdfPhotoSize } from './report-pdf-layout';
import type { EmbeddableImage } from './report-pdf-render';

/**
 * GETTING EVIDENCE INTO THE PDF — M10 / S10-04.
 *
 * **THE SHAPE IS KNOWN WITHOUT DOWNLOADING ANYTHING.** Layout needs only the
 * aspect ratio, and the row already carries it: `source_width`/`source_height`
 * are the archive's encoded dimensions and `source_orientation` says whether the
 * axes are swapped. The display derivative was produced from the same picture
 * with the ratio preserved, so the page can be planned before a single byte is
 * fetched — and each photograph is then downloaded exactly once, at the moment
 * it is needed, for the box it will actually occupy.
 *
 * **200 DPI AT PRINTED SIZE, WHICH IS THE ONLY HONEST TARGET.** A 2048px photo
 * in a 3.5in box is 585 DPI of detail no printer or screen will ever show, and
 * forty of them is a 20 MB file that an older Android cannot assemble. The
 * picture is re-encoded to what its printed size can use — **never cropped,
 * never stretched, never squashed, and never upscaled** past what the display
 * copy actually contains.
 *
 * **ONE AT A TIME.** Callers process photographs sequentially and release each
 * one before the next. Decoding a dozen 2048px images at once is exactly the
 * memory spike this avoids.
 */

/** What a printed box needs, in pixels, at the locked target resolution. */
export const PRINT_DPI = 200;
const POINTS_PER_INCH = 72;

export function printPixelTarget(pointSize: number, dpi: number = PRINT_DPI): number {
  if (!Number.isFinite(pointSize) || pointSize <= 0) return 1;
  return Math.max(1, Math.ceil((pointSize / POINTS_PER_INCH) * dpi));
}

/**
 * The picture's UPRIGHT shape, from the row alone.
 *
 * Orientations 5–8 exchange the axes, and the derivative was rendered upright —
 * so a 4000×3000 sensor image stored sideways is a 3000×4000 photograph, and a
 * layout that believed the raw numbers would lay a portrait out as a landscape.
 */
export function photoSizeFromRow(row: CapturePhotoRow): PdfPhotoSize {
  const swapped = archiveAxesSwapped(row.source_orientation);
  const width = swapped ? row.source_height : row.source_width;
  const height = swapped ? row.source_width : row.source_height;
  if (width > 0 && height > 0) return { width, height };
  // A row without usable dimensions still gets a sane box rather than a
  // division by zero; the picture itself is unaffected.
  return { width: 4, height: 3 };
}

function base64FromBytes(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 0x8000;
  for (let index = 0; index < bytes.length; index += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(index, index + CHUNK));
  }
  return btoa(binary);
}

export function bytesToDataUrl(bytes: Uint8Array, mime: string): string {
  return `data:${mime};base64,${base64FromBytes(bytes)}`;
}

export class ReportPhotoUnavailable extends Error {
  constructor(
    /** Client-safe context: which Capture, which group. NEVER a URL or an id. */
    readonly context: { readonly capture: string; readonly group: 'Before' | 'After' },
  ) {
    super('report-photo-unavailable');
    this.name = 'ReportPhotoUnavailable';
  }
}

export interface PhotoFetcher {
  /** Signed-URL read of the DISPLAY derivative. Never the archive (INV-T14). */
  fetchDisplayBytes(row: CapturePhotoRow): Promise<Uint8Array>;
}

/**
 * Reading one photograph, with the one retry that actually matters.
 *
 * **SIGNED LINKS LAST FIVE MINUTES AND A BIG REPORT TAKES LONGER.** A link
 * signed at the start of generation is expired by the twentieth photograph, so
 * every read signs immediately before it fetches, and a failure is retried once
 * with a freshly signed link before it is called a failure. Beyond that the
 * caller aborts the whole document — a client report missing evidence Brandon
 * chose is worse than no report at all.
 */
export function createPhotoFetcher(urls: PhotoImageUrlPort, fetchImpl = fetch): PhotoFetcher {
  const read = async (row: CapturePhotoRow): Promise<Uint8Array | null> => {
    const path = serverPhotoObjectPath(row, 'display');
    const url = await urls.signedUrl(path);
    if (!url) return null;
    try {
      const response = await fetchImpl(url);
      if (!response.ok) return null;
      return new Uint8Array(await response.arrayBuffer());
    } catch {
      return null;
    }
  };

  return {
    async fetchDisplayBytes(row) {
      const first = await read(row);
      if (first) return first;
      const second = await read(row);
      if (second) return second;
      // The caller owns the user-facing wording; this throw carries no URL.
      throw new Error('report-photo-fetch-failed');
    },
  };
}

export interface PreparePhotoInput {
  readonly bytes: Uint8Array;
  readonly sourceMime: 'image/jpeg' | 'image/png';
  /** The box the planner assigned, in points. Drives the pixel target. */
  readonly boxWidthPt: number;
  readonly boxHeightPt: number;
  readonly raster: ImageRasterPort;
}

/** JPEG stays JPEG, PNG stays PNG — jsPDF embeds both without re-compressing. */
export function pdfImageFormat(mime: 'image/jpeg' | 'image/png'): 'JPEG' | 'PNG' {
  return mime === 'image/png' ? 'PNG' : 'JPEG';
}

/**
 * Re-encode one photograph for the box it will occupy.
 *
 * **ORIENTATION IS DELIBERATELY `null`.** The display derivative was already
 * rendered upright and carries no EXIF; passing the original orientation here
 * would rotate an upright picture a second time (I-040's exact failure).
 */
export async function preparePhotoImage(input: PreparePhotoInput): Promise<EmbeddableImage> {
  const longEdgePt = Math.max(input.boxWidthPt, input.boxHeightPt);
  const target = printPixelTarget(longEdgePt);

  const rendered = await input.raster.render({
    bytes: input.bytes,
    sourceMime: input.sourceMime,
    orientation: null,
    // `render` never upscales, so a display copy smaller than the print target
    // is embedded as it is rather than invented into more pixels.
    longEdge: target,
    encodeMime: input.sourceMime,
    quality: 0.85,
  });

  return {
    dataUrl: bytesToDataUrl(rendered.bytes, input.sourceMime),
    format: pdfImageFormat(input.sourceMime),
  };
}
